"use client";

import { useState } from "react";
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { useAssociates } from '@/data/associates';

export default function AssociatesList() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [limit, setLimit] = useState<number | "all">(10);
  const { associates } = useAssociates();

  const filteredAssociates = associates.filter(
    (associate) =>
      associate.nome_associado.toLowerCase().includes(search.toLowerCase()) &&
      (selectedLetter === "" || associate.nome_associado.toUpperCase().startsWith(selectedLetter))
  );

  const displayedAssociates = limit === "all" ? filteredAssociates : filteredAssociates.slice(0, limit);

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const availableLetters = new Set(
    associates.map((associate) => associate.nome_associado.charAt(0).toUpperCase())
  );

  return (
    <div className="p-6 max-w-6xl mx-auto text-left" id="associates">
      <h1 className="text-3xl font-semibold text-[#4A6548]">{t("associates.title")}</h1>
      
      <div className="flex flex-wrap gap-2 my-4">
        {alphabet.map((letter) => (
          <button
            key={letter}
            onClick={() => availableLetters.has(letter) && setSelectedLetter(letter === selectedLetter ? "" : letter)}
            className={`px-2 py-1 border rounded-md ${
              availableLetters.has(letter) ? "text-[#4A6548]" : "text-gray-400 cursor-not-allowed"
            } ${selectedLetter === letter ? "bg-brand-blue/30" : ""}`}
            disabled={!availableLetters.has(letter)}
          >
            {letter}
          </button>
        ))}
      </div>
      
      <input
        type="text"
        placeholder={t("associates.search_placeholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded-md my-4"
      />
      
      <div className="flex items-center gap-4 mb-4">
        <label className="text-[#4A6548]">{t("associates.show")}: </label>
        <select
          value={limit}
          onChange={(e) => setLimit(e.target.value === "all" ? "all" : parseInt(e.target.value))}
          className="p-2 border border-gray-300 rounded-md"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value="all">{t("associates.all")}</option>
        </select>
      </div>
      
      <div className="space-y-4 text-left">
        {displayedAssociates.map((associate) => (
          <div key={associate.id_associado} className="border-b pb-2">
            <p className="text-lg font-medium text-[#4A6548]">{associate.nome_associado}</p>
            <Link 
              href={`/associate/${associate.id_associado}`}
              className="text-[#4A6548] flex items-center gap-1 hover:text-[#4A6548]/80 transition-colors"
            >
              {t("associates.view_profile")}
            </Link>
          </div>
        ))}
        {displayedAssociates.length === 0 && (
          <p className="text-gray-500">{t("associates.no_results")}</p>
        )}
      </div>
    </div>
  );
}
